import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { NAV } from "@/constants/testIds";

const QUICK = [
  "How do I apply for a Solar Rooftop subsidy?",
  "Where can I track my Net Metering application?",
  "What CBG schemes are open now?",
  "How to register for tenders on PEDA?",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Sat Sri Akal! I'm your PEDA AI Assistant. Ask me about solar, biogas, EV, subsidies, tenders or citizen services." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sid });
      setSid(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${formatErr(e.response?.data?.detail) || e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid={NAV.chatbotFab}
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[hsl(var(--primary))] text-white shadow-2xl grid place-items-center hover:scale-105 transition-transform"
        aria-label="Open PEDA AI Assistant"
      >
        {open ? <X className="h-6 w-6"/> : <MessageSquare className="h-6 w-6"/>}
      </button>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(560px,calc(100vh-8rem))] rounded-2xl glass shadow-2xl flex flex-col overflow-hidden" data-testid="chatbot-panel">
          <div className="bg-peda-navy text-white p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary))] grid place-items-center">
              <Sparkles className="h-4 w-4"/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-semibold">PEDA AI Assistant</div>
              <div className="text-[10px] opacity-70 uppercase tracking-widest">Powered by Claude Sonnet 4.5</div>
            </div>
          </div>
          <div ref={boxRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-3.5 py-2.5 text-muted-foreground animate-pulse">Thinking…</div>
              </div>
            )}
          </div>
          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)} className="text-[11px] px-2.5 py-1 rounded-full border border-border hover:bg-muted">
                  {q}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e)=>{e.preventDefault(); send();}} className="p-3 border-t border-border flex items-center gap-2">
            <input
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              placeholder="Ask about PEDA schemes…"
              className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-[hsl(var(--primary))]"
              data-testid="chatbot-input"
            />
            <button type="submit" disabled={loading} className="h-9 w-9 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center disabled:opacity-50" data-testid="chatbot-send">
              <Send className="h-4 w-4"/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
